import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import axios from '../../../../api/axios';
import AddProductModal from '../../../../utils/modals/products/AddProductModal';
import EditProductModal from '../../../../utils/modals/products/EditProductModal';
import { GlobalContext } from '../../../../store/GlobalContext';
import { getFallbackProductImage, resolveProductImage } from '../../../../data/productsApi';

type CategoryOption = {
    _id: string;
    name: string;
};

type ProductItem = {
    _id: string;
    name: string;
    companyName?: string;
    description: string;
    price: number;
    stock: number;
    sku?: string;
    subcategory?: string;
    sizes?: string[];
    tags?: string[];
    images?: string[];
    isFeatured?: boolean;
    isActive: boolean;
    discount?: number;
    category?: { _id?: string; name?: string } | string;
};

const Products = () => {
    const { state } = useContext(GlobalContext);
    const token = state?.auth?.token;

    const [show, setShow] = useState(false);
    const [products, setProducts] = useState<ProductItem[]>([]);
    const [categories, setCategories] = useState<CategoryOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [subcategoryFilter, setSubcategoryFilter] = useState('all');
    const [sizeFilter, setSizeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [sortBy, setSortBy] = useState<'createdAt' | 'name' | 'price' | 'stock' | 'subcategory'>('createdAt');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [filterProducts, setFilterProducts] = useState<ProductItem[]>([]);

    const [editProduct, setEditProduct] = useState<ProductItem | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteCandidate, setDeleteCandidate] = useState<ProductItem | null>(null);

    const queryParams = useMemo(() => {
        const params: Record<string, string | number | boolean> = {
            page,
            limit,
            sortBy,
            sortOrder,
        };

        if (statusFilter !== 'all') {
            params.isActive = statusFilter === 'active';
        }

        if (search.trim()) {
            params.search = search.trim();
        }

        if (categoryFilter !== 'all') {
            params.category = categoryFilter;
        }

        if (subcategoryFilter !== 'all') {
            params.subcategory = subcategoryFilter;
        }

        if (sizeFilter !== 'all') {
            params.sizes = sizeFilter;
        }

        return params;
    }, [page, limit, sortBy, sortOrder, statusFilter, search, categoryFilter, subcategoryFilter, sizeFilter]);

    const subcategoryOptions = useMemo(() => {
        return Array.from(
            new Set(
                filterProducts
                    .map((product) => product.subcategory?.trim())
                    .filter((value): value is string => Boolean(value)),
            ),
        ).sort((left, right) => left.localeCompare(right));
    }, [filterProducts]);

    const sizeOptions = useMemo(() => {
        return Array.from(
            new Set(
                filterProducts
                    .flatMap((product) => (Array.isArray(product.sizes) ? product.sizes : []))
                    .map((value) => value.trim())
                    .filter(Boolean),
            ),
        ).sort((left, right) => left.localeCompare(right));
    }, [filterProducts]);

    const fetchFilterProducts = useCallback(async () => {
        try {
            const response = await axios.get('/products', {
                params: {
                    page: 1,
                    limit: 100,
                    sortBy: 'name',
                    sortOrder: 'asc',
                },
            });

            const list = Array.isArray(response.data?.products)
                ? response.data.products
                : Array.isArray(response.data)
                    ? response.data
                    : [];

            setFilterProducts(list);
        } catch {
            setFilterProducts([]);
        }
    }, []);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await axios.get('/categories', {
                params: {
                    page: 1,
                    limit: 100,
                    isActive: true,
                    sortBy: 'name',
                    sortOrder: 'asc',
                },
            });

            const list = Array.isArray(response.data?.categories)
                ? response.data.categories
                : Array.isArray(response.data)
                    ? response.data
                    : [];
            setCategories(list);
        } catch {
            setCategories([]);
        }
    }, []);

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get('/products', { params: queryParams });
            const list = Array.isArray(response.data?.products)
                ? response.data.products
                : Array.isArray(response.data)
                    ? response.data
                    : [];

            setProducts(list);
            setTotal(typeof response.data?.total === 'number' ? response.data.total : list.length);
            setTotalPages(typeof response.data?.totalPages === 'number' ? response.data.totalPages : 0);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to load products.');
            setProducts([]);
            setTotal(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    }, [queryParams]);

    useEffect(() => {
        fetchCategories();
        fetchFilterProducts();
    }, [fetchCategories, fetchFilterProducts]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    useEffect(() => {
        if (!error && !success) return;
        const timer = setTimeout(() => {
            setError('');
            setSuccess('');
        }, 3000);
        return () => clearTimeout(timer);
    }, [error, success]);

    const handleEditClick = (product: ProductItem) => {
        setEditProduct(product);
        setShowEditModal(true);
    };

    const openDeleteDialog = async (productId: string) => {
        setError('');
        setSuccess('');

        try {
            const response = await axios.get(`/products/${productId}`);
            setDeleteCandidate(response.data);
            setShowDeleteDialog(true);
        } catch {
            const fallback = products.find((product) => product._id === productId) || null;
            setDeleteCandidate(fallback);
            setShowDeleteDialog(true);
        }
    };

    const handleDelete = async () => {
        if (!deleteCandidate?._id || !token) {
            return;
        }

        setError('');
        setSuccess('');
        try {
            await axios.delete(`/products/${deleteCandidate._id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSuccess('Product deleted successfully.');
            setShowDeleteDialog(false);
            setDeleteCandidate(null);
            await Promise.all([fetchProducts(), fetchFilterProducts()]);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to delete product.');
        }
    };

    const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setPage(1);
        setSearch(searchInput);
    };

    const handleClearSearch = () => {
        setSearchInput('');
        setSearch('');
        setPage(1);
    };

    const canGoPrev = page > 1;
    const canGoNext = totalPages > 0 && page < totalPages;

    return (
        <>
            <AddProductModal
                show={show}
                setShow={setShow}
                token={token}
                categories={categories}
                onSaved={async () => {
                    setSuccess('Product created successfully.');
                    await Promise.all([fetchProducts(), fetchFilterProducts()]);
                }}
            />
            <EditProductModal
                show={showEditModal}
                setShow={setShowEditModal}
                token={token}
                categories={categories}
                product={editProduct}
                onSaved={async () => {
                    setSuccess('Product updated successfully.');
                    await Promise.all([fetchProducts(), fetchFilterProducts()]);
                }}
            />

            <Card className="p-3 admin-section-card">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0">Products</h4>
                    <Button variant="dark" onClick={() => setShow(true)}>Add Product</Button>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <div className="d-flex flex-wrap gap-2 mb-3 admin-toolbar-row">
                    <Form onSubmit={handleSearch} className="d-flex gap-2 flex-grow-1 admin-search-form">
                        <Form.Control
                            placeholder="Search by name, description, or SKU"
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            aria-label="Search products"
                        />
                        <Button type="submit" variant="outline-dark">Search</Button>
                        <Button type="button" variant="outline-secondary" onClick={handleClearSearch}>Clear</Button>
                    </Form>

                    <Form.Select
                        className="admin-filter-select"
                        value={categoryFilter}
                        onChange={(event) => {
                            setPage(1);
                            setCategoryFilter(event.target.value);
                        }}
                        aria-label="Filter by category"
                    >
                        <option value="all">All Categories</option>
                        {categories.map((category) => (
                            <option key={category._id} value={category._id}>{category.name}</option>
                        ))}
                    </Form.Select>

                    <Form.Select
                        className="admin-filter-select"
                        value={subcategoryFilter}
                        onChange={(event) => {
                            setPage(1);
                            setSubcategoryFilter(event.target.value);
                        }}
                        aria-label="Filter by subcategory"
                    >
                        <option value="all">All Subcategories</option>
                        {subcategoryOptions.map((subcategory) => (
                            <option key={subcategory} value={subcategory}>{subcategory}</option>
                        ))}
                    </Form.Select>

                    <Form.Select
                        className="admin-filter-select"
                        value={sizeFilter}
                        onChange={(event) => {
                            setPage(1);
                            setSizeFilter(event.target.value);
                        }}
                        aria-label="Filter by size"
                    >
                        <option value="all">All Sizes</option>
                        {sizeOptions.map((size) => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </Form.Select>

                    <Form.Select
                        className="admin-filter-select admin-filter-sm"
                        value={statusFilter}
                        onChange={(event) => {
                            setPage(1);
                            setStatusFilter(event.target.value as 'all' | 'active' | 'inactive');
                        }}
                        aria-label="Filter by status"
                    >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </Form.Select>

                    <Form.Select
                        className="admin-filter-select admin-filter-sm"
                        value={sortBy}
                        onChange={(event) => {
                            setPage(1);
                            setSortBy(event.target.value as 'createdAt' | 'name' | 'price' | 'stock' | 'subcategory');
                        }}
                        aria-label="Sort by"
                    >
                        <option value="createdAt">Sort: Created</option>
                        <option value="name">Sort: Name</option>
                        <option value="price">Sort: Price</option>
                        <option value="stock">Sort: Stock</option>
                        <option value="subcategory">Sort: Subcategory</option>
                    </Form.Select>

                    <Form.Select
                        className="admin-filter-select admin-filter-xs"
                        value={sortOrder}
                        onChange={(event) => {
                            setPage(1);
                            setSortOrder(event.target.value as 'asc' | 'desc');
                        }}
                        aria-label="Sort order"
                    >
                        <option value="desc">Desc</option>
                        <option value="asc">Asc</option>
                    </Form.Select>

                    <Form.Select
                        className="admin-filter-select admin-filter-xs"
                        value={limit}
                        onChange={(event) => {
                            setPage(1);
                            setLimit(Number(event.target.value));
                        }}
                        aria-label="Rows per page"
                    >
                        <option value={5}>5 / page</option>
                        <option value={10}>10 / page</option>
                        <option value={20}>20 / page</option>
                    </Form.Select>
                </div>

                <Table striped bordered hover responsive className="admin-data-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Image</th>
                            <th>Name</th>
                            <th>Company</th>
                            <th>Description</th>
                            <th>Category</th>
                            <th>Subcategory</th>
                            <th>Sizes</th>
                            <th>Price</th>
                            <th>Stock</th>
                            <th>SKU</th>
                            <th>Tags</th>
                            <th>Status</th>
                            <th>Featured</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!loading && products.length === 0 ? (
                            <tr>
                                <td colSpan={15} className="text-center">No products found.</td>
                            </tr>
                        ) : (
                            products.map((product, index) => {
                                const productCategoryName = typeof product.category === 'object'
                                    ? product.category?.name
                                    : categories.find((item) => item._id === product.category)?.name;
                                const primaryImage = Array.isArray(product.images) && product.images.length > 0
                                    ? product.images[0]
                                    : '';
                                const imageUrl = primaryImage ? resolveProductImage(primaryImage) : getFallbackProductImage();

                                return (
                                    <tr key={product._id}>
                                        <td>{(page - 1) * limit + index + 1}</td>
                                        <td>
                                            <img
                                                src={imageUrl}
                                                alt={product.name}
                                                width={48}
                                                height={48}
                                                style={{ objectFit: 'cover', borderRadius: '6px' }}
                                                onError={(event) => {
                                                    event.currentTarget.src = getFallbackProductImage();
                                                }}
                                            />
                                        </td>
                                        <td>{product.name}</td>
                                        <td>{product.companyName || '-'}</td>
                                        <td>{product.description || '-'}</td>
                                        <td>{productCategoryName || '-'}</td>
                                        <td>{product.subcategory || '-'}</td>
                                        <td>{Array.isArray(product.sizes) && product.sizes.length > 0 ? product.sizes.join(', ') : '-'}</td>
                                        <td>${Number(product.price || 0).toFixed(2)}</td>
                                        <td>{product.stock}</td>
                                        <td>{product.sku || '-'}</td>
                                        <td>
                                            {Array.isArray(product.tags) && product.tags.length > 0
                                                ? product.tags.map((tag) => <Badge key={tag} bg="info" className="me-1">{tag}</Badge>)
                                                : 'No Tags'}
                                        </td>
                                        <td>
                                            {product.isActive ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge>}
                                        </td>
                                        <td>
                                            {product.isFeatured ? <Badge bg="dark">Featured</Badge> : <Badge bg="light" text="dark">No</Badge>}
                                        </td>
                                        <td>
                                            <Button className="me-2" variant="warning" size="sm" onClick={() => handleEditClick(product)}>
                                                Edit
                                            </Button>
                                            <Button variant="danger" size="sm" onClick={() => openDeleteDialog(product._id)}>
                                                Delete
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </Table>

                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 admin-pagination-row">
                    <small>Showing {products.length} of {total} products</small>
                    <div className="d-flex align-items-center gap-2">
                        <Button variant="outline-secondary" size="sm" disabled={!canGoPrev} onClick={() => setPage((prev) => prev - 1)}>
                            Prev
                        </Button>
                        <span>Page {page}{totalPages > 0 ? ` of ${totalPages}` : ''}</span>
                        <Button variant="outline-secondary" size="sm" disabled={!canGoNext} onClick={() => setPage((prev) => prev + 1)}>
                            Next
                        </Button>
                    </div>
                </div>
            </Card>

            <Modal show={showDeleteDialog} onHide={() => { setShowDeleteDialog(false); setDeleteCandidate(null); }}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Product</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2">You are about to delete this product:</p>
                    <p className="mb-0"><strong>{deleteCandidate?.name || 'Selected Product'}</strong></p>
                    {deleteCandidate?.description ? (
                        <small className="text-muted">{deleteCandidate.description}</small>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowDeleteDialog(false); setDeleteCandidate(null); }}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        Confirm Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default Products;

